import React from 'react';
import { FileText, Mic, Sparkles } from 'lucide-react';
import { callStatus } from '@/lib/dict';
import type { CallStatus } from '@/lib/api/types';

/**
 * Колонка «Обработка» в плотных таблицах — три значка, всегда все три.
 *
 * Запись, транскрипт, резюме стоят на одних и тех же местах в каждой
 * строке, поэтому взгляд читает столбец, а не строку: видно, что резюме нет
 * ни у кого, хотя записи есть у всех. Пока показывались только имеющиеся
 * значки, они прыгали и такого сравнения не давали.
 *
 * Цвет говорит о состоянии, а не о наличии:
 *
 * - **обычный** — сделано;
 * - **приглушённый** — ещё не сделано, но будет;
 * - **красный** — не сделано из-за сбоя, и само по себе не появится.
 *
 * Когда обрабатывать нечего — прочерк вместо значков: три приглушённых
 * обещали бы, что запись и разбор ещё появятся, а появиться им неоткуда.
 * Это не ошибка, и красным такую строку красить тоже нельзя.
 *
 * Условие прочерка — **по фактическому отсутствию всего**, а не по статусу
 * `skipped`. В базе нашлись звонки со `skipped`, у которых есть и запись, и
 * транскрипт (см. журнал плана): по статусу мы спрятали бы за прочерком
 * запись, которую человек может послушать. Отсутствие проверяемо, статус —
 * нет.
 *
 * Отсюда полезное следствие: у сорвавшегося звонка микрофон может остаться
 * обычным, а транскрипт покраснеть — запись приехала, расшифровка упала. Из
 * одного цвета на всю строку этого было не видно.
 *
 * Статус словом и текст ошибки — в подсказке каждого значка: в колонке они
 * занимали место и повторялись в каждой строке.
 */

const tones = {
  done: 'text-brand-light',
  pending: 'text-slate-200',
  failed: 'text-red-500',
} as const;

export default function ProcessingCell({
  status,
  errorMessage,
  hasRecording,
  hasTranscript,
  hasSummary,
}: {
  status: CallStatus;
  errorMessage?: string | null;
  hasRecording: boolean;
  hasTranscript: boolean;
  hasSummary: boolean;
}) {
  const term = callStatus[status];
  const failed = status === 'error';

  /** Статус или ошибка — вторая половина подсказки у каждого значка. */
  const state = failed
    ? `Ошибка: ${errorMessage || term.hint || 'обработка не удалась'}`
    : errorMessage
      ? // Сообщение показываем и при не-ошибочном статусе: такие строки в
        // базе встречаются, и молча его терять нельзя — это единственный
        // след того, что с звонком что-то происходило.
        `${term.label} · ${errorMessage}`
      : term.hint
        ? `${term.label} — ${term.hint}`
        : term.label;

  // Ждать больше нечего и ничего не появилось — показывать нечем.
  const settled = status === 'skipped' || status === 'done';
  if (settled && !hasRecording && !hasTranscript && !hasSummary) {
    return (
      <span className="text-xs text-slate-300" title={state}>
        —
      </span>
    );
  }

  const items = [
    {
      key: 'rec',
      node: <Mic size={13} />,
      has: hasRecording,
      yes: 'Есть запись',
      no: 'Записи нет',
    },
    {
      key: 'txt',
      node: <FileText size={13} />,
      has: hasTranscript,
      yes: 'Есть транскрипт',
      no: 'Транскрипта нет',
    },
    {
      key: 'sum',
      node: <Sparkles size={13} />,
      has: hasSummary,
      yes: 'Есть резюме',
      no: 'Резюме нет',
    },
  ];

  return (
    <div className="flex items-center gap-1.5">
      {items.map((item) => {
        // Красным помечаем только недостающее: то, что успело появиться до
        // сбоя, сбоем не стало.
        const tone = item.has ? tones.done : failed ? tones.failed : tones.pending;
        const what = item.has ? item.yes : item.no;
        return (
          <span
            key={item.key}
            className={`inline-flex ${tone}`}
            title={`${what} · ${state}`}
            aria-label={`${what} · ${state}`}
          >
            {item.node}
          </span>
        );
      })}
    </div>
  );
}
